import { z } from 'zod';

// Only the fields we read; Deepgram's response carries much more and unknown keys are stripped.
const alternativeSchema = z.object({
    transcript: z.string(),
    confidence: z.number(),
    words: z.array(z.object({ confidence: z.number() })).default([]),
    // Present when `language=multi`: one entry per language detected in the alternative.
    languages: z.array(z.string()).optional(),
});

const deepgramResponseSchema = z.object({
    metadata: z.object({
        request_id: z.string(),
        // Seconds of audio Deepgram actually decoded — compare with the client-side recording duration.
        duration: z.number(),
        model_info: z.record(z.string(), z.object({ name: z.string(), version: z.string() })).default({}),
    }),
    results: z.object({
        channels: z.array(
            z.object({
                alternatives: z.array(alternativeSchema),
                detected_language: z.string().optional(),
            }),
        ),
    }),
});

const lowConfidenceThreshold = 0.5;

export type TranscriptionResult = {
    transcript: string;
    requestId: string;
    audioDurationS: number;
    confidence: number;
    lowConfidenceWordCount: number;
    detectedLanguages: string[];
    modelVersion: string;
};

export function parseDeepgramResponse(json: unknown): TranscriptionResult {
    const { metadata, results } = deepgramResponseSchema.parse(json);
    const channel = results.channels[0];
    const alternative = channel?.alternatives[0];
    const model = Object.values(metadata.model_info)[0];

    return {
        transcript: alternative?.transcript ?? '',
        requestId: metadata.request_id,
        audioDurationS: metadata.duration,
        confidence: alternative?.confidence ?? 0,
        lowConfidenceWordCount:
            alternative?.words.filter((w) => w.confidence < lowConfidenceThreshold).length ?? 0,
        detectedLanguages:
            alternative?.languages ?? (channel?.detected_language ? [channel.detected_language] : []),
        modelVersion: model ? `${model.name}@${model.version}` : 'unknown',
    };
}
